import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import { useHistory } from 'react-router-dom';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import useMode from '../../hooks/useMode';
import waterServiceColumns, {
  companyServiceAddressColumns,
} from './CustomerThirdPane.columns';
import { Customer, ServiceAddress } from './types';
import DataTable from '../Table/DataTable';
import { Flex, Text } from 'rebass';
import StatusChip from '../Status/StatusChip';

type CustomerDetailProps = {
  customerDetail: Customer;
};

const CustomerDetail: React.FC<CustomerDetailProps> = ({ customerDetail }) => {
  const history = useHistory();
  const mode = useMode();

  const [serviceIds, setServiceIds] = useState(
    customerDetail.serviceAddresses
      ? customerDetail.serviceAddresses.map((a) => a.address.id)
      : []
  );

  const waterServices = useMemo(() => {
    const indexed = customerDetail.serviceAddresses.reduce(
      (indexed, serviceAddress) => {
        indexed[serviceAddress.address.id] = serviceAddress;
        return indexed;
      },
      {} as Record<string, ServiceAddress>
    );

    return Object.keys(indexed)
      .map((key) => indexed[key])
      .sort((a, b) => {
        if (mode === 'water-supplier') {
          const aPoints = +a.active + +(a.waterService?.status === 'ACTIVE');
          const bPoints = +b.active + +(b.waterService?.status === 'ACTIVE');
          return bPoints - aPoints;
        }
        return +b.active - +a.active;
      });
  }, [mode, customerDetail.serviceAddresses]);

  return (
    <>
      <Accordion title="Account">
        {customerDetail.accountNumber && (
          <AccordionItem label="Number" value={customerDetail.accountNumber} />
        )}
        <AccordionItem
          label="Status"
          value={constantToLabel(customerDetail.status)}
          status={customerDetail.status === 'ACTIVE' ? 'success' : 'error'}
          kind="chip"
        />
        {customerDetail.notes && (
          <AccordionItem label="Notes" value={customerDetail.notes} />
        )}
        {customerDetail.tags && customerDetail.tags.length > 0 && (
          <AccordionItem
            label="Tags"
            value={
              <Flex sx={{ gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {customerDetail.tags.map((tag) => (
                  <StatusChip status="default" key={tag}>
                    {tag}
                  </StatusChip>
                ))}
              </Flex>
            }
          />
        )}
      </Accordion>
      <Accordion title="Contact">
        <AccordionItem label="Name" value={customerDetail.profile.name} />
        {customerDetail.profile.phones.map((phone, idx) => (
          <AccordionItem
            key={`${phone.id}-${idx}`}
            label={
              phone.subType === 'UNKNOWN' ? `Phone` : `Phone (${phone.subType})`
            }
            value={phone.value}
          />
        ))}
        {customerDetail.profile.emails.map((email, idx) => (
          <AccordionItem
            key={`${email.id}-${idx}`}
            label={
              email.subType === 'UNKNOWN' ? `Email` : `Email (${email.subType})`
            }
            value={email.value}
          />
        ))}
        {customerDetail.profile.contactAddress && (
          <AccordionItem
            label="Mailing Address"
            value={<Address address={customerDetail.profile.contactAddress} />}
          />
        )}
        {customerDetail.profile.billingAddress && (
          <AccordionItem
            label="Billing Address"
            value={<Address address={customerDetail.profile.billingAddress} />}
          />
        )}
      </Accordion>
      <Accordion title="Service Addresses">
        {waterServices && waterServices.length ? (
          <Flex maxHeight={350}>
            <DataTable
              columns={
                mode === 'water-supplier'
                  ? waterServiceColumns
                  : companyServiceAddressColumns
              }
              itemIds={serviceIds}
              items={waterServices}
              onRowClick={
                mode === 'water-supplier'
                  ? (item: ServiceAddress) => {
                      if (item.waterService) {
                        history.push(
                          `/${mode}/water-services/${item.waterService.id}`
                        );
                      }
                    }
                  : undefined
              }
            />
          </Flex>
        ) : (
          <Text variant="body2" color="fieldBorderHover">
            There are no water services associated with this customer.
          </Text>
        )}
      </Accordion>
    </>
  );
};

CustomerDetail.propTypes = {
  customerDetail: PropTypes.any,
};

export default CustomerDetail;
